import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// Gemeinsame Teile für den Kopf der Produktseiten und der All-in-One-Seite (Vorlage JerrySFX-Shop):
// links die Box mit den Ansichten aus der Extension (Reiter liegen mit in der Box), rechts die
// Textspalte. Beide sind gleich hoch, ohne das Bild zu beschneiden.

// Glas-Rand + Licht um den Cursor (effekte.js der Demo)
export const Fx = () => <><span className="fx-licht" /><span className="fx-rand" /></>;

// Titelbilder (products/titel/<name>.jpg, 2560 px) gibt es verkleinert als WebP unter /800/ und /1600/.
// klein: Karten (800er, 1600er für scharfe Bildschirme); gross: Box oben (1600er, das Original nur bei Bedarf)
// Englische Fassungen liegen gleich aufgebaut unter products/titel/en/ (…/en/800, …/en/1600).
const TITEL = /^\/products\/titel\/(en\/)?([^/]+)\.jpg$/;
export const titelPfad = (src, en) => (en && /^\/products\/titel\/[^/]+\.jpg$/.test(src || '') ? src.replace('/products/titel/', '/products/titel/en/') : src);
export function titelBild(src, gross, sizes) {
  const m = TITEL.exec(src || '');
  if (!m) return { src };
  const w = breite => `/products/titel/${m[1] || ''}${breite}/${m[2]}.webp`;
  return gross
    ? { src: w(1600), srcSet: `${w(1600)} 1600w, ${src} 2560w`, sizes }
    : { src: w(800), srcSet: `${w(800)} 800w, ${w(1600)} 1600w`, sizes };
}

export function Bild({ a }) {
  if (!a) return null;
  return a.video
    ? <video src={a.src} muted loop autoPlay playsInline />
    : <>{a.titelleiste && <div className="ptitel">{a.titelleiste}</div>}<img {...titelBild(a.src, true, '(max-width: 960px) 100vw, 60vw')} alt="" /></>;
}

// Staubteilchen im Lichtschein (einmal ausgewürfelt)
export function Staub() {
  const teile = useMemo(() => Array.from({ length: 46 }, () => {
    const gross = Math.random() < .12;
    return { left: (45 + Math.random() * 55) + '%', top: (Math.random() * 70) + '%', '--d': (7 + Math.random() * 8) + 's', '--v': (-Math.random() * 12) + 's',
      '--op': (.3 + Math.random() * .6).toFixed(2), ...(gross ? { width: 5, height: 5, filter: 'blur(2px)' } : {}) };
  }), []);
  return <div className="staub">{teile.map((st, i) => <i key={i} style={st} />)}</div>;
}

// Box mit den Ansichten; sie wechseln alle 5 s, bis jemand selbst einen Reiter antippt
export function AnsichtBox({ ansichten }) {
  const [heldI, setHeldI] = useState(0);
  const [festgehalten, setFestgehalten] = useState(false);
  const ruhig = useMemo(() => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches, []);
  useEffect(() => {
    if (festgehalten || ruhig || ansichten.length < 2) return undefined;
    const t = setTimeout(() => setHeldI(i => (i + 1) % ansichten.length), 5000);
    return () => clearTimeout(t);
  }, [heldI, festgehalten, ruhig, ansichten.length]);
  return (
    <div className="buehne">
      <div className="halo" />
      <div className="karte">
        {ansichten.map((a, i) => <div key={a.src + i} className={'ansicht' + (i === heldI ? '' : ' weg')}><Bild a={a} /></div>)}
        {ansichten.length > 1 && (
          <div className="hero-reiter">
            {ansichten.map((a, i) => (
              <button key={a.name} className={'hreiter' + (i === heldI ? ' an' + (festgehalten ? ' fest' : '') : '')}
                onClick={() => { setFestgehalten(true); setHeldI(i); }}>{a.kurz ? <><span className="lang">{a.name}</span><span className="kurz">{a.kurz}</span></> : a.name}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Box und Textspalte gleich hoch: ist der Text höher als die 16:9-Box, wird die Box-Spalte breiter
// (bis 66 %), bis beide gleich hoch sind. Gibt die Ref für .hero-innen zurück.
// Über 960 px stehen beide nebeneinander (darunter Box oben, Text darunter, siehe produktseite.css).
export function useGleichHoch(abhaengig) {
  const heroInnen = useRef(null);
  useLayoutEffect(() => {
    const innen = heroInnen.current;
    if (!innen) return undefined;
    const info = innen.querySelector('.info');
    let warten = 0;
    const anpassen = () => {
      warten = 0;
      innen.style.gridTemplateColumns = '';
      if (window.innerWidth <= 960 || !info) return;
      const gap = parseFloat(getComputedStyle(innen).columnGap) || 0;
      const frei = innen.clientWidth - gap;
      let box = frei * 1.25 / 2.25;
      for (let i = 0; i < 4; i++) {
        innen.style.gridTemplateColumns = `${box}px minmax(0, 1fr)`;
        info.style.alignSelf = 'start';
        const h = info.offsetHeight;
        info.style.alignSelf = '';
        const noetig = Math.min(h * 16 / 9, frei * .66);
        if (noetig <= box + 1) break;
        box = noetig;
      }
    };
    const planen = () => { if (!warten) warten = requestAnimationFrame(anpassen); };
    anpassen();
    const ro = new ResizeObserver(planen);
    ro.observe(innen);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(planen);
    return () => { ro.disconnect(); cancelAnimationFrame(warten); };
  }, abhaengig);
  return heroInnen;
}
